import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";

import { multiresumeAuthProviderSchema } from "./auth-provider";

export const multiresumeDeployEnvSchema = z.enum(["development", "test", "staging", "production"]);
export const multiresumeDeployEnvWithDefaultSchema = multiresumeDeployEnvSchema.default("development");

const localSupabaseJwtKeySchema = z
  .string()
  .regex(/^eyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*$/, "Expected a local Supabase CLI JWT key.");

export const supabasePublishableKeySchema = z.union([
  z.string().startsWith("sb_publishable_"),
  localSupabaseJwtKeySchema,
]);

export const supabaseSecretKeySchema = z.union([z.string().startsWith("sb_secret_"), localSupabaseJwtKeySchema]);

// Merge server runtime (process.env) with Vite client variables (import.meta.env).
// In a browser bundle, VITE_-prefixed values are injected by Vite into
// import.meta.env; process.env is unavailable. Server secrets must never be
// projected into VITE_-prefixed fields, so we copy import.meta.env entries
// verbatim and keep process.env separate.
// Pure merge of server process.env and Vite import.meta.env. Exported for tests.
// Server secrets are passed through as-is and never renamed into VITE_ fields.
export function mergeRuntimeEnv(
  serverVars: Record<string, string | undefined>,
  metaEnv: Record<string, string | undefined>,
): Record<string, string | undefined> {
  return { ...serverVars, ...metaEnv };
}

function buildRuntimeEnv(): Record<string, string | undefined> {
  const serverVars = process.env as Record<string, string | undefined>;
  // import.meta.env exists in Vite builds; guard for non-Vite runtimes.
  const metaEnv =
    typeof import.meta !== "undefined" && import.meta.env
      ? (import.meta.env as Record<string, string | undefined>)
      : {};
  return mergeRuntimeEnv(serverVars, metaEnv);
}

export const env = createEnv({
  clientPrefix: "VITE_",
  runtimeEnv: buildRuntimeEnv(),
  emptyStringAsUndefined: true,

  client: {
    VITE_MULTIRESUME_TURNSTILE_SITE_KEY: z.string().min(1).optional(),
    VITE_MULTIRESUME_CAPTCHA_TEST_TOKEN: z.string().min(1).optional(),
    VITE_MULTIRESUME_CORE_PRODUCT_LINE: z.enum(["phase5", "phase6b"]).optional(),
  },

  server: {
    // Server
    TZ: z.string().default("Etc/UTC"),
    MULTIRESUME_DEPLOY_ENV: multiresumeDeployEnvWithDefaultSchema,
    APP_URL: z.url({ protocol: /https?/ }),
    PRINTER_APP_URL: z.url({ protocol: /https?/ }).optional(),

    // Printer
    PRINTER_ENDPOINT: z.url({ protocol: /^(wss?|https?)$/ }),

    // Database
    DATABASE_URL: z.url({ protocol: /postgres(ql)?/ }),

    // Authentication
    AUTH_SECRET: z.string().min(1),
    MULTIRESUME_AUTH_PROVIDER: multiresumeAuthProviderSchema,
    BETTER_AUTH_API_KEY: z.string().min(1).optional(),

    // Supabase Auth Spike
    SUPABASE_URL: z.url({ protocol: /https?/ }).optional(),
    SUPABASE_PUBLISHABLE_KEY: supabasePublishableKeySchema.optional(),
    SUPABASE_SECRET_KEY: supabaseSecretKeySchema.optional(),
    SUPABASE_AUTH_CALLBACK_URL: z.url({ protocol: /https?/ }).optional(),

    // Social Auth (Google)
    GOOGLE_CLIENT_ID: z.string().min(1).optional(),
    GOOGLE_CLIENT_SECRET: z.string().min(1).optional(),

    // Social Auth (GitHub)
    GITHUB_CLIENT_ID: z.string().min(1).optional(),
    GITHUB_CLIENT_SECRET: z.string().min(1).optional(),

    // Social Auth (LinkedIn)
    LINKEDIN_CLIENT_ID: z.string().min(1).optional(),
    LINKEDIN_CLIENT_SECRET: z.string().min(1).optional(),

    // Custom OAuth Provider
    OAUTH_PROVIDER_NAME: z.string().min(1).optional(),
    OAUTH_CLIENT_ID: z.string().min(1).optional(),
    OAUTH_CLIENT_SECRET: z.string().min(1).optional(),
    OAUTH_DISCOVERY_URL: z.url({ protocol: /https?/ }).optional(),
    OAUTH_AUTHORIZATION_URL: z.url({ protocol: /https?/ }).optional(),
    OAUTH_TOKEN_URL: z.url({ protocol: /https?/ }).optional(),
    OAUTH_USER_INFO_URL: z.url({ protocol: /https?/ }).optional(),
    OAUTH_SCOPES: z
      .string()
      .min(1)
      .transform((value) => value.split(" "))
      .default(["openid", "profile", "email"]),

    // Email (SMTP)
    SMTP_HOST: z.string().min(1).optional(),
    SMTP_PORT: z.coerce.number().int().min(1).max(65535).default(587),
    SMTP_USER: z.string().min(1).optional(),
    SMTP_PASS: z.string().min(1).optional(),
    SMTP_FROM: z.string().min(1).optional(),
    SMTP_SECURE: z.stringbool().default(false),

    // Storage (Optional)
    S3_ACCESS_KEY_ID: z.string().min(1).optional(),
    S3_SECRET_ACCESS_KEY: z.string().min(1).optional(),
    S3_REGION: z.string().default("us-east-1"),
    S3_ENDPOINT: z.url({ protocol: /https?/ }).optional(),
    S3_BUCKET: z.string().min(1).optional(),
    // Set to "true" for path-style URLs (endpoint/bucket), common with MinIO, SeaweedFS, etc.
    // Set to "false" for virtual-hosted-style URLs (bucket.endpoint), common with AWS S3, Cloudflare R2, etc.
    S3_FORCE_PATH_STYLE: z.stringbool().default(false),
    // Optional object ACL for providers that support/require it. Leave unset for providers like Cloudflare R2.
    S3_OBJECT_ACL: z.string().optional(),

    // MultiResume AI (MVP)
    MULTIRESUME_AI_API_KEY: z.string().min(1).optional(),
    MULTIRESUME_AI_BASE_URL: z.url({ protocol: /https?/ }).default("https://api.deepseek.com"),
    MULTIRESUME_AI_MODEL: z.string().min(1).default("deepseek-v4-flash"),
    MULTIRESUME_AI_REQUIRE_EXACT_RESPONSE_MODEL: z.stringbool().default(false),
    MULTIRESUME_AI_MAX_TOKENS: z.coerce.number().int().min(1).max(8192).default(8192),
    MULTIRESUME_AI_TIMEOUT_MS: z.coerce.number().int().min(1_000).default(60_000),
    MULTIRESUME_AI_PDF_IMPORT_DAILY_LIMIT: z.coerce.number().int().min(0).default(2),
    MULTIRESUME_AI_PDF_IMPORT_MONTHLY_LIMIT: z.coerce.number().int().min(0).default(3),
    // Explicit local-only PDF acceptance adapter. The adapter performs an additional runtime/origin safety check.
    MULTIRESUME_PDF_LOCAL_ACCEPTANCE_MODE: z.enum(["off", "fixture_v1"]).default("off"),
    // Explicit local-only JD proposal acceptance adapter. The adapter performs an additional runtime/origin safety check.
    MULTIRESUME_JD_PROPOSAL_LOCAL_ACCEPTANCE_MODE: z
      .enum(["off", "fixture_v1", "fixture_terminal_failure_v1", "fixture_terminal_then_success_v1"])
      .default("off"),
    MULTIRESUME_AI_SUMMARY_DAILY_LIMIT: z.coerce.number().int().min(0).default(5),
    MULTIRESUME_AI_SUMMARY_MONTHLY_LIMIT: z.coerce.number().int().min(0).default(10),
    MULTIRESUME_JD_OCR_PROVIDER: z.enum(["disabled", "openrouter"]).default("disabled"),
    // Explicit local-only JD OCR acceptance adapter. The adapter performs an additional runtime/origin safety check.
    MULTIRESUME_JD_OCR_LOCAL_ACCEPTANCE_MODE: z.enum(["off", "fixture_v1"]).default("off"),
    MULTIRESUME_JD_OCR_API_KEY: z.string().min(1).optional(),
    MULTIRESUME_JD_OCR_BASE_URL: z.url({ protocol: /https?/ }).default("https://openrouter.ai/api/v1"),
    MULTIRESUME_JD_OCR_MODEL: z.string().min(1).default("google/gemma-3-12b-it"),
    MULTIRESUME_JD_OCR_MAX_TOKENS: z.coerce.number().int().min(1).max(8192).default(2048),
    MULTIRESUME_JD_OCR_TIMEOUT_MS: z.coerce.number().int().min(1_000).default(30_000),

    // MultiResume legal/support configuration
    MULTIRESUME_LEGAL_NAME: z.string().min(1).default("Ted Xi"),
    MULTIRESUME_SERVICE_URL: z.url({ protocol: /https?/ }).optional(),
    MULTIRESUME_SUPPORT_EMAIL: z.email().default("support@multiresu.me"),
    MULTIRESUME_PRIVACY_EMAIL: z.email().default("privacy@multiresu.me"),
    MULTIRESUME_OPERATOR_COUNTRY: z.string().min(1).optional(),

    // MultiResume operator/readiness metadata
    MULTIRESUME_INTERNAL_HEALTH_TOKEN: z.string().min(16).optional(),
    MULTIRESUME_INVITE_HASH_SECRET: z.string().min(32).optional(),
    MULTIRESUME_ABUSE_HASH_SECRET: z.string().min(32).optional(),
    // MultiResume Phase 6 abuse challenge (Turnstile / local test adapter)
    MULTIRESUME_CAPTCHA_MODE: z.enum(["turnstile", "test_adapter"]).optional(),
    MULTIRESUME_TURNSTILE_SECRET_KEY: z.string().min(1).optional(),
    MULTIRESUME_BUILD_SHA: z.string().min(1).optional(),
    MULTIRESUME_IMAGE_DIGEST: z
      .string()
      .regex(/^sha256:[0-9a-f]{64}$/)
      .optional(),
    MULTIRESUME_DEPLOYMENT_ID: z.string().min(1).optional(),
    MULTIRESUME_DEPLOYMENT_TIMESTAMP: z.string().min(1).optional(),
    MULTIRESUME_AI_QUALITY_RELEASE_LABEL: z.string().min(1).optional(),
    MULTIRESUME_AI_QUALITY_RELEASE_DEPLOYED_AT: z.string().datetime().optional(),
    MULTIRESUME_AI_QUALITY_RELEASE_WORKFLOW_VERSION: z.string().min(1).optional(),
    MULTIRESUME_AI_QUALITY_RELEASE_PROMPT_VERSION: z.string().min(1).optional(),
    MULTIRESUME_AI_QUALITY_RELEASE_MODEL_VERSION: z.string().min(1).optional(),
    MULTIRESUME_AI_QUALITY_RELEASE_SCHEMA_VERSION: z.string().min(1).optional(),
    MULTIRESUME_AI_QUALITY_JUDGE_MODEL_VERSION: z.literal("deepseek-v4-pro").optional(),
    MULTIRESUME_AI_QUALITY_JUDGE_PROMPT_VERSION: z.string().min(1).optional(),
    MULTIRESUME_AI_QUALITY_JUDGE_SCHEMA_VERSION: z.string().min(1).optional(),

    // MultiResume Phase 4 payment/billing
    MULTIRESUME_PAYMENT_MODE: z.enum(["disabled", "sandbox", "test", "live"]).default("disabled"),
    // Explicit loopback-only local Waffo authority fixture; never enabled by default.
    MULTIRESUME_WAFFO_PANCAKE_LOCAL_ACCEPTANCE_MODE: z.string().trim().min(1).default("off"),
    MULTIRESUME_PHASE4_PAYMENT_MODE: z.enum(["disabled", "sandbox", "live"]).optional(),
    MULTIRESUME_PADDLE_ENVIRONMENT: z.enum(["sandbox", "live"]).default("sandbox"),
    MULTIRESUME_PADDLE_CHECKOUT_URL: z.url({ protocol: /https?/ }).optional(),
    MULTIRESUME_PADDLE_CLIENT_TOKEN: z
      .union([z.string().startsWith("test_"), z.string().startsWith("live_")])
      .optional(),
    MULTIRESUME_PADDLE_API_KEY: z.string().min(1).optional(),
    MULTIRESUME_PADDLE_WEBHOOK_SECRET: z.string().min(1).optional(),
    MULTIRESUME_PADDLE_STARTER_PACK_PRICE_ID: z.string().min(1).optional(),
    MULTIRESUME_STARTER_PACK_QUOTA_UNITS: z.coerce.number().int().min(0).optional(),
    MULTIRESUME_STARTER_PACK_PRICE_DISPLAY: z.string().min(1).optional(),
    MULTIRESUME_STARTER_PACK_AMOUNT_MINOR: z.coerce.number().int().min(0).optional(),
    MULTIRESUME_STARTER_PACK_CURRENCY: z.string().min(1).optional(),
    MULTIRESUME_STARTER_PACK_VALIDITY_DAYS: z.coerce.number().int().min(0).optional(),
    MULTIRESUME_STARTER_PACK_PER_ACCOUNT_CAP: z.coerce.number().int().min(0).optional(),
    MULTIRESUME_PHASE4_PAYMENT_TERMS_VERSION: z.string().min(1).optional(),
    MULTIRESUME_REFUND_POLICY_VERSION: z.string().min(1).optional(),

    // Feature Flags
    FLAG_DEBUG_PRINTER: z.stringbool().default(false),
    FLAG_DISABLE_SIGNUPS: z.stringbool().default(false),
    FLAG_DISABLE_EMAIL_AUTH: z.stringbool().default(false),
    FLAG_DISABLE_IMAGE_PROCESSING: z.stringbool().default(false),
    FLAG_DISABLE_MULTIRESUME_AI: z.stringbool().default(false),
    FLAG_DISABLE_PDF_IMPORT_AI: z.stringbool().default(false),
    FLAG_DISABLE_SUMMARY_AI: z.stringbool().default(false),
    FLAG_DISABLE_MULTIRESUME_PURCHASES: z.stringbool().default(true),
  },
});
